import { getSafeApiUrl } from '../config/environment';

export type ServiceType = 'GATEWAY' | 'INVENTORY' | 'ORDER' | 'IDENTITY' | 'NOTIFICATION';

export interface ApiUrlValidationResult {
  valid: boolean;
  errors: string[];
  warnings: string[];
}

/**
 * API URL 중앙 관리 클래스
 * 모든 서비스의 API URL을 일관되게 관리하고 검증
 */
export class ApiUrlManager {
  private static instance: ApiUrlManager;
  private apiUrls: Map<ServiceType, string> = new Map();
  private lastValidation: Date | null = null;

  private constructor() {
    // 싱글톤 패턴으로 인스턴스 생성 방지
  }

  static getInstance(): ApiUrlManager {
    if (!this.instance) {
      this.instance = new ApiUrlManager();
    }
    return this.instance;
  }

  /**
   * 특정 서비스의 API URL 가져오기
   */
  getApiUrl(service: ServiceType): string {
    // 캐시된 URL이 있으면 반환
    const cached = this.apiUrls.get(service);
    if (cached) {
      return cached;
    }

    // 환경 설정에서 URL 가져오기
    try {
      const url = getSafeApiUrl(service);
      this.apiUrls.set(service, url);
      console.log(`[ApiUrlManager] ${service} URL 설정: ${url}`);
      return url;
    } catch (error) {
      console.error(`[ApiUrlManager] ${service} URL 생성 실패:`, error);
      throw new Error(`${service} 서비스의 API URL을 생성할 수 없습니다.`);
    }
  }

  /**
   * 모든 서비스의 API URL 사전 로드
   */
  preloadAllUrls(): void {
    const services: ServiceType[] = ['GATEWAY', 'INVENTORY', 'ORDER', 'IDENTITY', 'NOTIFICATION'];
    
    services.forEach(service => {
      try {
        this.getApiUrl(service);
      } catch (error) {
        console.error(`[ApiUrlManager] ${service} URL 사전 로드 실패:`, error);
      }
    });

    console.log('[ApiUrlManager] 모든 API URL 사전 로드 완료');
  }

  /**
   * 모든 API URL 유효성 검증
   */
  validateAllUrls(): ApiUrlValidationResult {
    const errors: string[] = [];
    const warnings: string[] = [];
    const services: ServiceType[] = ['GATEWAY', 'INVENTORY', 'ORDER', 'IDENTITY', 'NOTIFICATION'];

    services.forEach(service => {
      try {
        const url = this.getApiUrl(service);
        
        // URL 형식 검증
        if (!url) {
          errors.push(`${service}: URL이 비어있습니다`);
          return;
        }

        if (!url.startsWith('http://') && !url.startsWith('https://')) {
          errors.push(`${service}: 유효하지 않은 프로토콜 - ${url}`);
          return;
        }

        // URL 구조 검증
        try {
          const urlObj = new URL(url);
          
          // localhost가 아닌데 http 사용 시 경고
          if (urlObj.protocol === 'http:' && !urlObj.hostname.includes('localhost')) {
            warnings.push(`${service}: 프로덕션에서 HTTP 사용 중 - ${url}`);
          }

          // /api 경로 포함 여부 확인
          if (!url.includes('/api')) {
            warnings.push(`${service}: URL에 /api 경로가 없습니다 - ${url}`);
          }

        } catch (urlError) {
          errors.push(`${service}: 잘못된 URL 형식 - ${url}`);
        }

      } catch (error) {
        errors.push(`${service}: URL 생성 실패 - ${error}`);
      }
    });

    this.lastValidation = new Date();
    
    const result = {
      valid: errors.length === 0,
      errors,
      warnings
    };

    // 결과 로깅
    if (result.valid) {
      console.log('[ApiUrlManager] 모든 API URL 검증 통과');
      if (warnings.length > 0) {
        console.warn('[ApiUrlManager] 경고 사항:', warnings);
      }
    } else {
      console.error('[ApiUrlManager] API URL 검증 실패:', errors);
    }

    return result;
  }

  /**
   * URL 캐시 초기화
   */
  clearCache(): void {
    this.apiUrls.clear();
    console.log('[ApiUrlManager] URL 캐시 초기화됨');
  }

  /**
   * 특정 서비스 URL 강제 업데이트
   */
  refreshUrl(service: ServiceType): string {
    this.apiUrls.delete(service);
    return this.getApiUrl(service);
  }

  /**
   * 현재 설정된 모든 URL 정보 가져오기
   */
  getAllUrls(): Record<ServiceType, string> {
    const urls: Partial<Record<ServiceType, string>> = {};
    const services: ServiceType[] = ['GATEWAY', 'INVENTORY', 'ORDER', 'IDENTITY', 'NOTIFICATION'];

    services.forEach(service => {
      try {
        urls[service] = this.getApiUrl(service);
      } catch (error) {
        console.error(`[ApiUrlManager] ${service} URL 조회 실패:`, error);
      }
    });

    return urls as Record<ServiceType, string>;
  }

  /**
   * 마지막 검증 시간 가져오기
   */
  getLastValidationTime(): Date | null {
    return this.lastValidation;
  }

  /**
   * API URL 상태 정보
   */
  getStatus(): {
    cacheSize: number;
    lastValidation: Date | null;
    isHealthy: boolean;
  } {
    const validation = this.validateAllUrls();
    
    return {
      cacheSize: this.apiUrls.size,
      lastValidation: this.lastValidation,
      isHealthy: validation.valid
    };
  }
}

// 브라우저 전역 객체에 디버깅용 인스턴스 추가
if (typeof window !== 'undefined') {
  (window as any).apiUrlManager = ApiUrlManager.getInstance();
  console.log('[ApiUrlManager] 브라우저 전역 객체에 등록됨. window.apiUrlManager로 접근 가능');
}

// 기본 인스턴스 내보내기
export const apiUrlManager = ApiUrlManager.getInstance();
export default ApiUrlManager;